import React from 'react';
import { cx } from './styles';

/**
 * TicketsPage's local `StatCard`, promoted to shared with its variants,
 * `tabular-nums` and optional subtitle intact. Same pixels, one definition.
 */
export type StatTone = 'default' | 'green' | 'blue' | 'amber';

export interface StatTileProps {
  label: string;
  value: number | string;
  subtitle?: string;
  color?: StatTone;
}

const borderColors: Record<StatTone, string> = {
  default: 'border-white/10',
  green: 'border-emerald-500/30',
  blue: 'border-blue-500/30',
  amber: 'border-amber-500/30',
};

const valueColors: Record<StatTone, string> = {
  default: 'text-white',
  green: 'text-emerald-400',
  blue: 'text-blue-400',
  amber: 'text-amber-400',
};

export const StatTile: React.FC<StatTileProps> = ({ label, value, subtitle, color = 'default' }) => (
  <div className={cx('rounded-xl border bg-white/[0.02] p-4', borderColors[color])}>
    <p className="mb-1 text-[11px] font-medium uppercase tracking-wider text-white/40">{label}</p>
    <p className={cx('text-2xl font-semibold tabular-nums', valueColors[color])}>{value}</p>
    {subtitle && <p className="mt-0.5 text-xs text-white/30">{subtitle}</p>}
  </div>
);

/**
 * Grid, never a horizontal scroller: two-up on a phone beats a scroller the
 * user has to discover. `cols` caps the widest breakpoint.
 */
export const StatTileRow: React.FC<{
  cols?: 2 | 3 | 4 | 5;
  className?: string;
  children: React.ReactNode;
}> = ({ cols = 5, className, children }) => {
  const wide: Record<number, string> = {
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-3',
    4: 'grid-cols-2 sm:grid-cols-2 lg:grid-cols-4',
    5: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5',
  };
  return (
    <div className={cx('grid gap-3', cols <= 3 ? `grid-cols-1 ${wide[cols]}` : wide[cols], className)}>
      {children}
    </div>
  );
};
